import { useMemo } from 'react'

import type { DateRange } from 'react-day-picker'
import { useQuery } from 'react-query'

import { SubscriberAnalysisService } from '@/modules/tracking/new-subscriber-analysis/service'
import type {
  SubscribersAnalysisDataUI,
  SubscribersStatusType,
} from '@/modules/tracking/new-subscriber-analysis/types.ts'
import {
  InteractionOverviewStatusValues,
  SubscriptionLengthStatusValues,
} from '@/modules/tracking/new-subscriber-analysis/types.ts'
import { handleError } from '@/utils/error'

function findStatusValue<T>(
  targetArray: SubscribersStatusType<T>[],
  targetValue: T,
) {
  return targetArray.find((s) => s.status === targetValue)?.count || 0
}

export const useSubscribersAnalysis = (
  dateRange: DateRange,
  creatorId: number,
) => {
  const {
    data: subscribersAnalysis,
    isFetching: isSubscribersAnalysisLoading,
    refetch,
  } = useQuery(
    ['subscribersAnalysis', creatorId, dateRange],
    ({ signal }) => {
      if (!creatorId) {
        return null
      }

      return SubscriberAnalysisService.getSubscribersAnalysis(
        creatorId,
        dateRange.from || new Date(),
        dateRange.to || new Date(),
        signal,
      )
    },
    {
      onError: (error) =>
        handleError(error, 'Failed to get subscribers analysis'),
    },
  )

  const mappedData = useMemo(() => {
    if (!subscribersAnalysis) {
      return null
    }

    const activeStatusCount = findStatusValue(
      subscribersAnalysis.subscriptionLength,
      SubscriptionLengthStatusValues.ACTIVE,
    )
    const expiredStatusCount = findStatusValue(
      subscribersAnalysis.subscriptionLength,
      SubscriptionLengthStatusValues.EXPIRED,
    )

    const ignoredOverviewStatusCount = findStatusValue(
      subscribersAnalysis.interactionOverview,
      InteractionOverviewStatusValues.IGNORED,
    )
    const repliedOverviewStatusCount = findStatusValue(
      subscribersAnalysis.interactionOverview,
      InteractionOverviewStatusValues.REPLIED,
    )
    const seenOverviewStatusCount = findStatusValue(
      subscribersAnalysis.interactionOverview,
      InteractionOverviewStatusValues.SEEN,
    )

    const subscribersAnalysisDataUI: SubscribersAnalysisDataUI = {
      ...subscribersAnalysis,
      subscriptionLengthData: {
        [SubscriptionLengthStatusValues.ACTIVE]: activeStatusCount,
        [SubscriptionLengthStatusValues.EXPIRED]: expiredStatusCount,
      },
      interactionOverviewData: {
        [InteractionOverviewStatusValues.IGNORED]: ignoredOverviewStatusCount,
        [InteractionOverviewStatusValues.REPLIED]: repliedOverviewStatusCount,
        [InteractionOverviewStatusValues.SEEN]: seenOverviewStatusCount,
      },
      subscriptionLengthSum: activeStatusCount + expiredStatusCount,
    }

    return subscribersAnalysisDataUI
  }, [subscribersAnalysis])

  return {
    subscribersAnalysis: mappedData,
    isFetching: isSubscribersAnalysisLoading,
    refetch: refetch,
  }
}
